import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { usePropertyContext } from "@/contexts/PropertyContext";
import { ChevronDown, Plus, Home, Check, Lock } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SuburbSelect } from "@/components/SuburbSelect";
import { useSubscription } from "@/hooks/useSubscription";
import UpgradePlanSheet from "@/components/UpgradePlanSheet";

const PageHeader = ({ pageName, className }) => {
    const { properties, activeProperty, setActiveProperty, addProperty, loading } = usePropertyContext();
    const { canAddProperty, plan, isPropertyLocked } = useSubscription();

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isUpgradeOpen, setIsUpgradeOpen] = useState(false);
    const [newPropertyName, setNewPropertyName] = useState("");
    const [newPropertySuburb, setNewPropertySuburb] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const handleOpenAddModal = () => {
        if (!canAddProperty) {
            setIsUpgradeOpen(true);
            return;
        }
        setIsAddModalOpen(true);
    };

    const handlePropertySwitch = async (propertyId) => {
        if (propertyId !== activeProperty?.id) {
            await setActiveProperty(propertyId);
        }
    };

    const handleAddProperty = async () => {
        if (!newPropertyName.trim() || !newPropertySuburb) return;

        setIsSubmitting(true);
        const result = await addProperty({
            name: newPropertyName.trim(),
            suburb: newPropertySuburb,
        });

        if (result) {
            setNewPropertyName("");
            setNewPropertySuburb("");
            setIsAddModalOpen(false);
        }
        setIsSubmitting(false);
    };

    const displayName = activeProperty?.name || activeProperty?.address || "Select Property";

    return (
        <>
            <header
                className={cn(
                    "fixed top-0 left-0 right-0 z-40 transition-all duration-300 ease-in-out border-b border-gray-100",
                    isScrolled ? "bg-white/80 backdrop-blur-md shadow-sm" : "bg-white/60 backdrop-blur-2xl",
                    className
                )}
                style={{
                    paddingTop: isScrolled
                        ? "calc(env(safe-area-inset-top, 0px) + 0.5rem)"
                        : "calc(env(safe-area-inset-top, 0px) + 1rem)",
                    paddingBottom: isScrolled ? "0.5rem" : "1rem"
                }}
            >
                <div className="max-w-lg mx-auto">
                    <div className="px-6 flex flex-col items-center gap-1">
                        <span
                            className={cn(
                                "text-[10px] font-bold uppercase tracking-[0.2em] text-primary/40 leading-none transition-all duration-300",
                                isScrolled ? "opacity-0 h-0 overflow-hidden" : "opacity-100 h-auto mb-1"
                            )}
                        >
                            {pageName}
                        </span>

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild disabled={isScrolled}>
                                <button
                                    className={cn(
                                        "flex items-center justify-center gap-2 group focus:outline-none transition-all duration-300",
                                        isScrolled ? "cursor-default" : "cursor-pointer"
                                    )}
                                    disabled={loading || isScrolled}
                                >
                                    <h2
                                        className={cn(
                                            "font-extrabold text-foreground tracking-tight transition-all duration-300",
                                            isScrolled ? "text-lg" : "text-2xl group-hover:text-primary"
                                        )}
                                    >
                                        {loading ? "Loading..." : displayName}
                                    </h2>
                                    <div
                                        className={cn(
                                            "bg-gray-50 p-1 rounded-full transition-all duration-300",
                                            isScrolled ? "opacity-0 w-0 p-0 overflow-hidden" : "opacity-100 group-hover:bg-primary/10"
                                        )}
                                    >
                                        <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                                    </div>
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="center"
                                className="w-72 bg-white/90 backdrop-blur-xl border-none shadow-2xl rounded-3xl p-2 z-50 mt-2"
                                sideOffset={8}
                            >
                                <div className="px-3 py-2">
                                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Your Properties</p>
                                    <div className="grid gap-1 max-h-[250px] overflow-y-auto pr-1">
                                        {(properties || []).map((property) => {
                                            const isLocked = isPropertyLocked(property.id);

                                            return (
                                                <DropdownMenuItem
                                                    key={property.id}
                                                    onSelect={() => {
                                                        handlePropertySwitch(property.id);
                                                    }}
                                                    className={cn(
                                                        "flex items-center gap-3 cursor-pointer p-3 rounded-2xl transition-all relative overflow-hidden",
                                                        property.id === activeProperty?.id ? "bg-primary/5 text-primary" : "hover:bg-gray-50",
                                                        isLocked && "opacity-75 grayscale"
                                                    )}
                                                >
                                                    {isLocked && (
                                                        <div className="absolute inset-0 z-10 bg-white/10 backdrop-blur-[1px]" />
                                                    )}
                                                    <div className={cn(
                                                        "w-10 h-10 rounded-xl flex items-center justify-center shrink-0 relative",
                                                        property.id === activeProperty?.id ? "bg-primary text-white" : (isLocked ? "bg-muted text-muted-foreground" : "bg-gray-100 text-muted-foreground")
                                                    )}>
                                                        <Home className="h-5 w-5" />
                                                        {isLocked && (
                                                            <div className="absolute -top-1 -right-1 bg-amber-500 rounded-full p-0.5 border border-white">
                                                                <Lock className="w-2.5 h-2.5 text-white" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <p className="font-bold truncate text-sm">{property.name}</p>
                                                            {isLocked && (
                                                                <span className="text-[10px] text-amber-600 font-bold uppercase tracking-tight bg-amber-50 px-1 rounded">Locked</span>
                                                            )}
                                                        </div>
                                                        {property.address && property.address !== property.name && (
                                                            <p className="text-[11px] opacity-70 truncate">{property.address}</p>
                                                        )}
                                                    </div>
                                                    {property.id === activeProperty?.id && !isLocked && (
                                                        <Check className="h-4 w-4 bg-primary/10 rounded-full p-0.5" />
                                                    )}
                                                </DropdownMenuItem>
                                            );
                                        })}
                                    </div>
                                </div>

                                <DropdownMenuSeparator className="bg-gray-50 mx-2" />

                                <DropdownMenuItem
                                    onClick={handleOpenAddModal}
                                    className={cn(
                                        "flex items-center gap-3 cursor-pointer p-3 rounded-2xl mt-1 transition-all",
                                        !canAddProperty ? "text-amber-600 hover:bg-amber-50" : "text-primary hover:bg-primary/5"
                                    )}
                                >
                                    <div className={cn(
                                        "w-10 h-10 rounded-xl flex items-center justify-center shrink-0",
                                        !canAddProperty ? "bg-amber-100" : "bg-primary/10"
                                    )}>
                                        {!canAddProperty ? <Lock className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
                                    </div>
                                    <span className="font-bold text-sm">
                                        {!canAddProperty ? "Upgrade to Add House" : "Add New House"}
                                    </span>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </header>

            <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Add New Property</DialogTitle>
                        <DialogDescription>Enter a nickname and suburb to create your new house profile.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="property-name" className="text-sm font-bold">Home Nickname *</Label>
                            <Input
                                id="property-name"
                                placeholder="e.g., Primary Residence, Holiday Flat"
                                value={newPropertyName}
                                onChange={(e) => setNewPropertyName(e.target.value)}
                                className="rounded-xl"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="property-suburb" className="text-sm font-bold">Suburb / Area *</Label>
                            <SuburbSelect
                                value={newPropertySuburb}
                                onSelect={(val) => setNewPropertySuburb(val)}
                                placeholder="Search your area..."
                                className="rounded-xl"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsAddModalOpen(false)}
                            disabled={isSubmitting}
                            className="rounded-xl font-bold"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAddProperty}
                            disabled={!newPropertyName.trim() || !newPropertySuburb || isSubmitting}
                            className="rounded-xl font-bold"
                        >
                            {isSubmitting ? "Adding..." : "Add House"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <UpgradePlanSheet
                open={isUpgradeOpen}
                onOpenChange={setIsUpgradeOpen}
                currentPlan={plan}
            />
        </>
    );
};

export default PageHeader;
